import {
  Alert,
  AlertTitle,
  IconButton,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  styled,
} from "~/common/mui";
import AddCommentOutlinedIcon from "@mui/icons-material/AddCommentOutlined";
import Tag from "~/common/components/Tag/Tag";
import {
  CaseStatus,
  type CaseDataObject,
} from "~/features/orders/orderDetails/components/ServiceCases/types";
import { useTranslation } from "react-i18next";
import { C_SURFACE_CONTAINER_HIGH } from "~/common/components/icon/Colors";
import { CORNER_SMALL } from "~/common/components/icon/Corners";
import { type Dispatch, type SetStateAction, useEffect, useState } from "react";
import {
  addCommentByCode,
  fetchServiceCasesByLineNumber,
} from "~/features/orders/orderDetails/components/ServiceCases/api";
import AddCommentModal from "~/features/orders/orderDetails/components/ServiceCases/AddCommentModal";
import { TooltipWithArrow } from "~/common/components/TooltipWithArrow";
import { getTagVariant } from "~/features/orders/orderDetails/components/ServiceCases/caseStatusPresentation";

export { getTagVariant } from "~/features/orders/orderDetails/components/ServiceCases/caseStatusPresentation";

type Props = {
  cases: CaseDataObject[];
  userHasCreatedCase?: boolean;
  orderNumber?: string;
  lineNumber?: string;
  showTableOnly?: boolean;
  showAddComment?: true;
  setSnackbar: Dispatch<SetStateAction<{ open: boolean; message: string }>>;
};

export const MainTableHeadElements = () => {
  const { t } = useTranslation();

  return (
    <>
      <TableCell sx={{ width: "156px" }}>
        <Typography variant="labelMedium" component={"b"}>
          {t("serviceCases.list.tableHeader.caseNumber")}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="labelMedium" component={"b"}>
          {t("serviceCases.list.tableHeader.subject")}
        </Typography>
      </TableCell>
      <TableCell sx={{ width: "180px" }}>
        <Typography variant="labelMedium" component={"b"}>
          {t("serviceCases.list.tableHeader.status")}
        </Typography>
      </TableCell>
    </>
  );
};
export const ServiceCasesTableV2 = ({
  cases,
  userHasCreatedCase,
  orderNumber,
  lineNumber,
  setSnackbar,
  showTableOnly = false,
  showAddComment = true,
}: Props) => {
  const { t } = useTranslation();
  const [caseData, setCaseData] = useState<CaseDataObject[] | undefined>(cases);
  const [commentModalCode, setCommentModalCode] = useState<string | undefined>(undefined);
  const isCaseProcessing = showTableOnly ? false : caseData?.some((item) => !item.completeData);

  useEffect(() => {
    if (showTableOnly) {
      return;
    }
    if (isCaseProcessing && orderNumber && lineNumber) {
      //Make call every 2 minutes
      const interval = setInterval(
        () => {
          fetchServiceCasesByLineNumber(orderNumber, lineNumber).then((res) => {
            setCaseData(res[lineNumber]);
          });
        },
        1000 * 60 * 2,
      );
      return () => clearInterval(interval);
    }
  }, [isCaseProcessing]);

  if (!caseData) {
    return;
  }

  const onAddCommentAction = (code: string, message: string) => {
    addCommentByCode(code, message);
    setSnackbar({
      open: true,
      message: t("serviceCases.commentSent.snackbarInfo"),
    });
  };

  const loadingBanner = (
    <Alert severity="info">
      <AlertTitle>{t("serviceCases.caseLoading.processing")}</AlertTitle>
      {t("serviceCases.caseLoading.label")}
    </Alert>
  );

  return (
    <>
      <Stack gap={2}>
        {commentModalCode && (
          <AddCommentModal
            onSend={onAddCommentAction}
            caseData={caseData?.find((item) => item.code === commentModalCode)}
            onClose={() => setCommentModalCode(undefined)}
          />
        )}
        <TableContainer>
          <Table sx={{ tableLayout: "fixed", bgcolor: "surfaceContainerLowest.main" }}>
            <TableHead>
              <StyledTableHeadRow>
                <MainTableHeadElements />
                {showAddComment && <TableCell sx={{ width: "40px" }} />}
              </StyledTableHeadRow>
            </TableHead>

            <TableBody>
              {caseData
                ?.filter((item) => item.completeData)
                .map((item) => (
                  <StyledTableRow key={item.sfCaseNumber}>
                    <TableCell>{item.sfCaseNumber}</TableCell>
                    <TableCell>{item.reasonOfRequest}</TableCell>
                    <TableCell sx={{ display: "flex" }}>
                      <Tag variant={getTagVariant(item.status)}> {t(CaseStatus[item.status])}</Tag>
                    </TableCell>

                    {showAddComment && (
                      <TableCell sx={{ padding: "8px 0" }}>
                        <TooltipWithArrow title={t("serviceCases.list.addComment.title")} placement="left">
                          <IconButton size="small" color="primary" onClick={() => setCommentModalCode(item.code)}>
                            <AddCommentOutlinedIcon />
                          </IconButton>
                        </TooltipWithArrow>
                      </TableCell>
                    )}
                  </StyledTableRow>
                ))}
              {!showTableOnly && (isCaseProcessing || userHasCreatedCase) && (
                <TableBodySkeleton showCommentColumn={!!showAddComment} row={1} />
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {!showTableOnly && (isCaseProcessing || userHasCreatedCase) && loadingBanner}
      </Stack>
    </>
  );
};

export const ServiceCaseTableV2Skeleton = ({ showCommentColumn = true }: { showCommentColumn?: boolean }) => {
  return (
    <TableContainer>
      <Table sx={{ tableLayout: "fixed" }}>
        <TableHead>
          <StyledTableHeadRow>
            <MainTableHeadElements />
            {showCommentColumn && <TableCell sx={{ width: "40px" }} />}
          </StyledTableHeadRow>
        </TableHead>
        <TableBody>
          <TableBodySkeleton showCommentColumn={showCommentColumn} />
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export const TableBodySkeleton = ({
  showCommentColumn = true,
  row = 3,
}: {
  showCommentColumn?: boolean;
  row?: number;
}) => {
  return (
    <>
      {Array.from({ length: row }).map((_, index) => (
        <StyledTableRow key={index}>
          <TableCell>
            <Typography>
              <Skeleton width={100} height={20} />
            </Typography>
          </TableCell>

          <TableCell>
            <Typography>
              <Skeleton width={100} height={20} />
            </Typography>
          </TableCell>

          <TableCell>
            <Skeleton variant="rounded" width={100} height={20} />
          </TableCell>

          {showCommentColumn && (
            <TableCell>
              <Skeleton variant="circular" width={20} height={20} />
            </TableCell>
          )}
        </StyledTableRow>
      ))}
    </>
  );
};

const TableContainer = styled("div")(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: CORNER_SMALL,
  overflow: "hidden",
}));

const StyledTableHeadRow = styled(TableRow)(({ theme }) => ({
  backgroundColor: theme.palette.surface.main,
  transition: "background-color 0.3s ease-in-out",
  "&:hover": {
    backgroundColor: `${theme.palette.surfaceContainerHighest} !important`,
    cursor: "pointer",
  },
}));

const StyledTableRow = styled(TableRow)({
  transition: "background-color 0.3s ease-in-out",
  "&:hover": {
    backgroundColor: `${C_SURFACE_CONTAINER_HIGH} !important`,
    cursor: "pointer",
  },
});
